import {Company, ContractedTime, LeavePeriod, Organisation, Resource, Roster, Deployment, AssignedDay, Unavailability, PersonalStamp, PersonalStampShift, Shift, DeploymentProposal} from '@cquential/api';
import {RosterData, RosterDay} from '@/types/types';
import {PlanbordResource, ResourceWithKeyedByDateObjects, PlanbordLeavePeriod} from '@/types/api-object-extensions';
import LeavePeriodHandler from '@/util/leaveperiodhandler';
import Dates from '@cquential/date-utils';
import ResourceEmploymentDeduplicator from '@/util/ResourceEmploymentDeduplicator';

export default class RosterConverter {
    public static convertRoster(base: Roster, id: number, start: Date, dayCount: number, organisation: Organisation, companies: Company[], shifts: Record<number, Shift>): RosterData {
        const target: RosterData = RosterConverter.initializeRosterData(id);
        target.beginDate = base.beginDate ? new Date(base.beginDate) : undefined;
        target.deriveRightsUntil = base.deriveRightsUntil ? new Date(base.deriveRightsUntil) : undefined;
        target.endDate = base.endDate ? new Date(base.endDate) : undefined;
        target.publishedUntil = base.publishedUntil ? new Date(base.publishedUntil) : undefined;
        target.proposedUntil = base.proposedUntil ? new Date(base.proposedUntil) : undefined;
        target.organisationId = base.organisationId;
        target.timeTypes = base.associativeTimeTypes;
        target.holidays = base.holidays!;
        target.unpublishedPartVisibleUntil = base.unpublishedPartVisibleUntil;
        target.wishInputClosedUntil = base.wishInputClosedUntil;
        target.wishInputEnabled = base.wishInputEnabled;
        target.secondaryPage = base.secondaryPage;
        target.usesMarketplaceFunctions = base.usesMarketplaceFunctions;
        target.comments = base.comments ? base.comments : undefined;
        target.rosterRankSet = base.rosterRankSets![0];
        target.titles = target.rosterRankSet?.titles ?? [];

        // Create array of dates for holidays
        const holidayDatesArray: Date[] = [];
        for (const holiday of target.holidays) {
            for (const date of holiday.specialDay.dates) {
                holidayDatesArray.push(date);
            }
        }

        // Build up dates object for rendering rows in table
        for (const date of base.days!) {
            target.days.push(this.createRosterDay(date, base, holidayDatesArray));
        }

        this.convertRosterResources(base, target, start, organisation, shifts);

        // Add data to target
        target.workloadRequirements = base.workloadRequirements;
        target.allowedFunctions = Object.freeze(base.allowedFunctions);

        for (const company of companies) {
            target.companies[company.id] = {
                ...company,
                deploymentsWithoutEmployee: {},
                employees: {},
            };
        }

        if (base.companyDeployments) {
            for (const deployment of base.companyDeployments) {
                if (target.companies[deployment.companyId!]) {
                    const dateString: string = Dates.Formatter.formatDateISO(deployment.date);

                    if (deployment.companyId) {
                        if (!target.companies[deployment.companyId]) {
                            console.error('Received deployment for non-existing company: ' + deployment.companyId!);
                        }

                        if (deployment.companyEmployeeId && deployment.companyEmployee) {
                            if (!target.companies[deployment.companyId].employees![deployment.companyEmployeeId]) {
                                target.companies[deployment.companyId].employees![deployment.companyEmployeeId] = {
                                    ...deployment.companyEmployee,
                                    companyId: deployment.companyId,
                                    deployments: {},
                                };
                            }
                            if (!target.companies[deployment.companyId].employees[deployment.companyEmployeeId].deployments![dateString]) {
                                target.companies[deployment.companyId].employees[deployment.companyEmployeeId].deployments![dateString] = [];
                            }
                            target.companies[deployment.companyId].employees[deployment.companyEmployeeId].deployments![dateString].push(deployment);
                        } else {
                            target.companies[deployment.companyId].deploymentsWithoutEmployee![dateString] ??= [];
                            target.companies[deployment.companyId].deploymentsWithoutEmployee![dateString].push(deployment);
                        }
                    }
                } else {
                    console.error('Received deployment for non-existing company: ' + deployment.companyId!);
                }
            }
        }

        if (base.openShifts) {
            for (const openShift of base.openShifts) {
                const date: string = Dates.Formatter.formatDateComputerFriendly(openShift.shiftDate);
                if (!(openShift.shiftId in target.openShifts)) {
                    target.openShifts[openShift.shiftId] = {};
                }

                if (!(date in target.openShifts[openShift.shiftId])) {
                    target.openShifts[openShift.shiftId][date] = [];
                }

                target.openShifts[openShift.shiftId][date].push(openShift);
            }
        }

        if (base.budgets) {
            target.budgets = base.budgets;
        }

        return target;
    }

    // Create initial RosterData object
    public static initializeRosterData(rosterId: number): RosterData {
        // Define object to give to the store
        return {
            rosterId,
            rosterRankSet: {
                rosterId: rosterId,
                beginDate: new Date(),
            },
            beginDate: new Date(),
            publishedUntil: new Date(),
            proposedUntil: new Date(),
            days: [],
            rosterResources: [],
            resourcesFromOtherRosters: [],
            externalResources: [],
            relatedResources: [],
            workloadRequirements: {},
            allowedFunctions: [],
            companies: {},
            organisationId: '',
            timeTypes: [],
            holidays: [],
            titles: [],
            budgets: [],
            unpublishedPartVisibleUntil: null,
            usesMarketplaceFunctions: false,
            wishInputClosedUntil: null,
            wishInputEnabled: true,
            openShifts: {},
        };
    }

    public static keyedByDate<T extends Deployment | DeploymentProposal | AssignedDay | Unavailability>(objects: T[]): Record<string, T[]> {
        const result: Record<string, T[]> = {};
        for (const o of objects) {
            const date: string = Dates.Formatter.formatDateISO(o.date);
            result[date] ??= [];
            result[date].push(o);
        }

        return result;
    }

    private static convertRosterResources(base: Roster, target: RosterData, start: Date, organisation: Organisation, shifts: Record<number, Shift>): void {
        ResourceEmploymentDeduplicator.deduplicateOnRoster(base);

        if (base.resources) {
            const rankPerResource: Record<number, number> = {};

            if (target.rosterRankSet?.resourceRosterRanks) {
                for (const resourceRosterRank of target.rosterRankSet.resourceRosterRanks) {
                    rankPerResource[resourceRosterRank.resourceId] = resourceRosterRank.rank;
                }
            }

            target.rosterResources = base.resources.map((resource: Resource): PlanbordResource => this.convertResourceToPlanbordResource(
                resource,
                organisation,
                rankPerResource[resource.id!] ?? Infinity,
                base,
            ));
            target.rosterResources.sort((a: PlanbordResource, b: PlanbordResource): number => a.rankInRoster - b.rankInRoster);
        }

        if (base.resourcesFromOtherRosters) {
            target.resourcesFromOtherRosters = base.resourcesFromOtherRosters.map((resourceFromOtherRoster: Resource): ResourceWithKeyedByDateObjects => RosterConverter.removeUnneededDataFromResourcesFromOtherRosters(
                RosterConverter.convertResourceToResourceWithKeyedDateObjects(resourceFromOtherRoster, base),
                target.rosterId,
                shifts,
            ));
        }

        if (base.relatedResources) {
            target.relatedResources = base.relatedResources.map((relatedResource: Resource): ResourceWithKeyedByDateObjects => RosterConverter.convertResourceToResourceWithKeyedDateObjects(relatedResource, base));
        }
    }

    /*
     * Usage of this method could be avoided by doing two separate API calls: one for resources on the roster, one for resources off the roster.
     * However, the added complexity of doing this, is not worth the bandwidth and database load that would be saved. The amount of data thrown away should be fairly limited.
     */
    private static removeUnneededDataFromResourcesFromOtherRosters(resource: ResourceWithKeyedByDateObjects, rosterId: number, shifts: Record<number, Shift>): ResourceWithKeyedByDateObjects {
        for (const date in resource.deployments) {
            resource.deployments[date] = resource.deployments[date].filter((deployment: Deployment): boolean => shifts[deployment.shiftId].rosterId === rosterId);
        }

        // You could flip these around if that's faster, i.e. first filter all stamp shifts, then remove stamps without stamp shifts. Current implementation is based on the guesstimation that the current implementation executes fewer checks in practice.
        resource.personalStamps = resource.personalStamps!.filter((personalStamp: PersonalStamp): boolean => personalStamp.personalStampShifts!.some((stampShift: PersonalStampShift): boolean => shifts[stampShift.shiftId].rosterId === rosterId));
        for (const stamp of resource.personalStamps) {
            stamp.personalStampShifts = stamp.personalStampShifts!.filter((stampShift: PersonalStampShift): boolean => shifts[stampShift.shiftId].rosterId === rosterId);
        }

        resource.sickPeriods = [];

        return resource;
    }

    private static convertResourceToResourceWithKeyedDateObjects(resource: Resource, roster: Roster): ResourceWithKeyedByDateObjects {
        let keyedUnavailabilities: Record<string, Unavailability[]> = {};
        if (roster.availabilityEnabled && resource.unavailabilities) {
            keyedUnavailabilities = RosterConverter.keyedByDate<Unavailability>(resource.unavailabilities);
        }

        return {
            ...resource,
            deployments: RosterConverter.keyedByDate<Deployment>(resource.deployments!),
            deploymentProposals: RosterConverter.keyedByDate<DeploymentProposal>(resource.deploymentProposals!),
            assignedDays: RosterConverter.keyedByDate<AssignedDay>(resource.assignedDays!),
            unavailabilities: keyedUnavailabilities,
        };
    }

    private static convertResourceToPlanbordResource(resource: Resource, organisation: Organisation, rankInRoster: number, roster: Roster): PlanbordResource {
        const resourceWithKeyedDeployments: ResourceWithKeyedByDateObjects = RosterConverter.convertResourceToResourceWithKeyedDateObjects(resource, roster);

        return {
            // copy all properties from the resource
            ...resourceWithKeyedDeployments,
            // overwrite some
            leavePeriods: resource.leavePeriods
                ? resource.leavePeriods.map((leavePeriod: LeavePeriod): PlanbordLeavePeriod => {
                        return LeavePeriodHandler.addProblematicDays(leavePeriod, resourceWithKeyedDeployments, organisation);
                    })
                : undefined,
            // add some
            rankInRoster: rankInRoster,
        };
    }

    private static createRosterDay(date: Date, roster: Roster, holidayDatesArray: Date[]): RosterDay {
        const dayNumber: number = Dates.Utils.getDay(date);

        return {
            ended: !!roster.endDate && date >= roster.endDate,
            published: !!roster.publishedUntil && date <= roster.publishedUntil,
            proposed: !!roster.proposedUntil && date <= roster.proposedUntil,
            holiday: holidayDatesArray.some((holidayDate: Date): boolean => holidayDate.valueOf() === date.valueOf()),
            dayName: Dates.Utils.DAY_NAMES[dayNumber],
            dayAbbreviation: Dates.Utils.SHORT_DAY_NAMES[dayNumber],
            weekend: dayNumber === 0 || dayNumber === 6,
            date,
            formattedDateOnly: Dates.Formatter.formatDateWithoutYear(date),
            formattedForComputers: Dates.Formatter.formatDateComputerFriendly(date),
            formattedDateIso: Dates.Formatter.formatDateISO(date),
        };
    }

    private static isContractedTimeRelevant(contractedTime: ContractedTime, rosterToCompareWith: RosterData, startDate: Date): boolean {
        return contractedTime.rosterId === rosterToCompareWith.rosterId && contractedTime.beginDate <= startDate && contractedTime.endDate >= startDate;
    }
}
